import { Component, Inject } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { CalendarLinkService } from "src/app/Services/calendar-link.service";

export type ScheduleViewDialogData = {
  entityType: "flow" | "bundle";
  title: string;
  linkUrl?: string;
};

export type CalendarProvider = "google" | "outlook" | "ical";

@Component({
  selector: "app-schedule-view-dialog",
  templateUrl: "./schedule-view-dialog.component.html",
  styleUrls: ["./schedule-view-dialog.component.scss"],
})
export class ScheduleViewDialogComponent {
  dateValue: string = this.todayDateString();
  timeValue: string = this.nowTimeStringRoundedTo5();
  durationMinutes: number = 30;
  selectedCalendar: CalendarProvider = "google";

  calendarOptions: Array<{
    value: CalendarProvider;
    label: string;
    iconPath: string;
    hint: string;
  }> = [
    {
      value: "google",
      label: "Google Calendar",
      iconPath: "assets/icons/calendar-google.svg",
      hint: "Open in browser",
    },
    {
      value: "outlook",
      label: "Outlook Calendar",
      iconPath: "assets/icons/calendar-outlook.svg",
      hint: "Open in browser",
    },
    {
      value: "ical",
      label: "iCalendar (.ics)",
      iconPath: "assets/icons/calendar-ical.svg",
      hint: "Download file",
    },
  ];

  get selectedCalendarIconPath(): string {
    return (
      this.calendarOptions.find((o) => o.value === this.selectedCalendar)
        ?.iconPath || "assets/icons/calendar-google.svg"
    );
  }

  constructor(
    private dialogRef: MatDialogRef<ScheduleViewDialogComponent, undefined>,
    @Inject(MAT_DIALOG_DATA) public data: ScheduleViewDialogData,
    private calendarLinkService: CalendarLinkService
  ) {}

  cancel(): void {
    this.dialogRef.close(undefined);
  }

  selectCalendar(type: CalendarProvider): void {
    this.selectedCalendar = type;
  }

  /**
   * Builds the event and opens/downloads the calendar entry synchronously within
   * the click handler so mobile browsers (iOS Safari etc.) do not block window.open.
   */
  schedule(): void {
    const start = this.composeLocalDateTime(this.dateValue, this.timeValue);
    if (!start || isNaN(start.getTime())) {
      return;
    }
    const duration = Math.max(5, Number(this.durationMinutes || 0));
    const end = new Date(start.getTime() + duration * 60 * 1000);

    const entityLabel =
      this.data.entityType === "bundle" ? "View bundle" : "Flowchest To Do";
    const entityKind =
      this.data.entityType === "bundle" ? "bundle" : "flow";
    const eventInput = {
      title: `${entityLabel}: ${this.data.title || ""}`.trim(),
      details: `Reminder to view this ${entityKind}.\n\n${this.data.linkUrl || ""}`,
      location: this.data.linkUrl || "",
      start,
      end,
    };

    if (this.selectedCalendar === "outlook") {
      const url = this.calendarLinkService.buildOutlookCalendarLink(eventInput);
      window.open(url, "_blank");
    } else if (this.selectedCalendar === "ical") {
      this.calendarLinkService.downloadIcsFile(
        eventInput,
        this.data.title || entityKind
      );
    } else {
      const url = this.calendarLinkService.buildGoogleCalendarLink(eventInput);
      window.open(url, "_blank");
    }

    this.dialogRef.close(undefined);
  }

  private composeLocalDateTime(dateStr: string, timeStr: string): Date | null {
    if (!dateStr || !timeStr) return null;
    const [y, m, d] = dateStr.split("-").map((v) => parseInt(v, 10));
    const [hh, mm] = timeStr.split(":").map((v) => parseInt(v, 10));
    if (!y || !m || !d || isNaN(hh) || isNaN(mm)) return null;
    return new Date(y, m - 1, d, hh, mm, 0, 0);
  }

  private todayDateString(): string {
    const now = new Date();
    const pad = (n: number) => `${n}`.padStart(2, "0");
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
      now.getDate()
    )}`;
  }

  private nowTimeStringRoundedTo5(): string {
    const now = new Date();
    const rounded = new Date(now);
    const minutes = rounded.getMinutes();
    const roundedMinutes = Math.ceil(minutes / 5) * 5;
    rounded.setMinutes(roundedMinutes, 0, 0);
    const pad = (n: number) => `${n}`.padStart(2, "0");
    return `${pad(rounded.getHours())}:${pad(rounded.getMinutes())}`;
  }
}
